// IndexedDB ラッパ（idb ライブラリ）
//
// stations ストア: 地点メタ + 調和定数 BLOB（732 bytes）を保存。
//   オフライン時、Web Worker がここから取り出して WASM で計算する。
// settings ストア: 任意の key/value 設定。

import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import type { StationMeta } from '@tideprecision/shared';
import type { WindForecastCache } from '../lib/windTypes.js';

interface CachedStation {
  meta: StationMeta;
  blob: ArrayBuffer; // 732 bytes
  lastFetched: number; // Date.now()
}

interface TideDB extends DBSchema {
  stations: {
    key: number; // station_id
    value: CachedStation;
  };
  windForecasts: {
    key: number; // station_id
    value: WindForecastCache;
  };
  settings: {
    key: string;
    value: unknown;
  };
}

const DB_NAME = 'tide-precision-v1';
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<TideDB>> | null = null;

function getDb(): Promise<IDBPDatabase<TideDB>> {
  if (!dbPromise) {
    dbPromise = openDB<TideDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('stations')) {
          db.createObjectStore('stations', { keyPath: 'meta.id' });
        }
        if (!db.objectStoreNames.contains('windForecasts')) {
          db.createObjectStore('windForecasts', { keyPath: 'stationId' });
        }
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }
      },
    });
  }
  return dbPromise;
}

export async function putStation(meta: StationMeta, blob: ArrayBuffer): Promise<void> {
  const db = await getDb();
  await db.put('stations', { meta, blob, lastFetched: Date.now() });
}

export async function getStation(id: number): Promise<CachedStation | undefined> {
  const db = await getDb();
  return db.get('stations', id);
}

export async function listStations(): Promise<CachedStation[]> {
  const db = await getDb();
  return db.getAll('stations');
}

export async function putWindForecast(value: WindForecastCache): Promise<void> {
  const db = await getDb();
  await db.put('windForecasts', value);
}

export async function getWindForecastCache(id: number): Promise<WindForecastCache | undefined> {
  const db = await getDb();
  return db.get('windForecasts', id);
}

export async function setSetting<T>(key: string, value: T): Promise<void> {
  const db = await getDb();
  await db.put('settings', value as unknown, key);
}

export async function getSetting<T>(key: string): Promise<T | undefined> {
  const db = await getDb();
  return (await db.get('settings', key)) as T | undefined;
}
